import React, { useState } from 'react';
import {
    Typography,
    Button,
    Input,
    Select,
    Option,
} from "@material-tailwind/react";
import {
    Plus,
    MessageSquare,
    AlertCircle,
    Paperclip,
    // Upload,
    RefreshCw,
    Search,
    ArrowRight,
    Calendar,
} from "lucide-react";
import { SUPPORT_TICKET_STATUSES_ENUM } from '../../utils/enums';
import { useNavigate } from 'react-router-dom';
import { useGetSupportTickets } from '../../custom-hooks/use-support';
import moment from 'moment';
import { SupportTicketCategoryIcon, SupportTicketPriorityConfig, SupportTicketStatusConfig } from '../../utils/ui-interpreters';
import { URLS } from '../../router-x/routes';



const CustomerSupportPortal = () => {
    const navigate = useNavigate();
    const { data: tickets, isLoading: isLoadingTickets, refetch: refetchTickets } = useGetSupportTickets();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');


    const getTimeAgo = (date) => {
        // const now = new Date();
        // const diff = now - new Date(date);
        // const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        // const hours = Math.floor(diff / (1000 * 60 * 60));
        // const minutes = Math.floor(diff / (1000 * 60));

        // if (days > 0) return `${days}d ago`;
        // if (hours > 0) return `${hours}h ago`;
        // if (minutes > 0) return `${minutes}m ago`;
        return moment(date).fromNow();
    };

    const handleViewTicket = (ticket) => {
        navigate(`${URLS.CUSTOMER.SUPPORT}/${ticket._id}`);
    };

    const handleCreateTicket = () => {
        navigate(`${URLS.CUSTOMER.SUPPORT}/create`);
    };

    const getFilteredTickets = () => {
        let filtered = tickets?.tickets || [];

        if (searchTerm) {
            filtered = filtered.filter(ticket =>
                ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                ticket._id.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (statusFilter !== 'all') {
            filtered = filtered.filter(ticket => ticket.status === statusFilter);
        }

        return filtered;
    };


    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto p-6">
                <div className="flex items-center justify-between">
                    {/* Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Support Center</h1>
                        <p className="text-gray-600">Submit and track your support requests</p>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex gap-4 mb-6">
                        <Button
                            variant="outlined"
                            size="sm"
                            className="flex items-center gap-3 border-gray-300 text-gray-700 rounded-full"
                            onClick={() => refetchTickets()}
                            disabled={isLoadingTickets}
                        >
                            <RefreshCw className={`h-5 w-5 ${isLoadingTickets ? 'animate-spin' : ''}`} />
                            Refresh
                        </Button>
                        <Button
                            size="sm"
                            className="flex items-center gap-3 rounded-full"
                            onClick={handleCreateTicket}
                        >
                            <Plus className="h-5 w-5" />
                            Create New Ticket
                        </Button>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white border border-gray-200 rounded-3xl p-6 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <Input
                                type="text"
                                label="Ticket ID or Subject"
                                placeholder="Search tickets..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                icon={<Search className="h-4 w-4" />}
                            // className="!border-gray-300 !bg-white"
                            />
                        </div>
                        <div>
                            <Select
                                label="Filter by Status"
                                value={statusFilter}
                                onChange={(value) => setStatusFilter(value)}
                            // className="!border-gray-300"
                            >
                                {
                                    SUPPORT_TICKET_STATUSES_ENUM.map((status) => {
                                        const statusConfig = SupportTicketStatusConfig(status);
                                        return (
                                            <Option key={status} value={status}>
                                                <div className="flex items-center gap-2">
                                                    <statusConfig.icon className={`h-4 w-4`} />
                                                    <span className="capitalize">{statusConfig?.label}</span>
                                                </div>
                                            </Option>
                                        )
                                    })
                                }
                            </Select>
                        </div>
                        <div className="flex items-center">
                            <Typography variant="small" className="text-gray-600">
                                {getFilteredTickets().length} of {tickets?.total || 0} tickets
                            </Typography>
                        </div>
                    </div>
                </div>

                {/* Tickets List */}
                {isLoadingTickets ? (
                    <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                        <RefreshCw className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600">Loading your tickets...</p>
                    </div>
                ) : getFilteredTickets().length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                        <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No tickets found</h3>
                        <p className="text-gray-600 mb-6">
                            {searchTerm || statusFilter !== 'all'
                                ? 'Try adjusting your search or filters'
                                : 'You haven\'t submitted any support tickets yet'
                            }
                        </p>
                        {!searchTerm && statusFilter === 'all' && (
                            <Button
                                className="rounded-full"
                                color="blue"
                                onClick={handleCreateTicket}
                            >
                                Create Your First Ticket
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {getFilteredTickets().map((ticket) => {
                            const statusConfig = SupportTicketStatusConfig(ticket.status);
                            const priorityConfig = SupportTicketPriorityConfig(ticket.priority);
                            const CategoryIcon = SupportTicketCategoryIcon(ticket.category);

                            return (
                                <div
                                    key={ticket._id}
                                    className="bg-white border border-gray-200 rounded-3xl p-6 hover:border-gray-300 transition-colors cursor-pointer"
                                    onClick={() => handleViewTicket(ticket)}
                                >
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <CategoryIcon className="h-5 w-5 text-gray-500" />
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                                    {ticket.subject}
                                                </h3>
                                                <p className="text-xs text-gray-600 uppercase">TKT-{ticket?._id.slice(-5)}</p>
                                            </div>
                                        </div>
                                        <ArrowRight className="h-5 w-5 text-gray-400" />
                                    </div>

                                    <p className="text-gray-700 mb-4 line-clamp-2">{ticket.description}</p>

                                    <div className="flex items-center gap-4 mb-4">
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusConfig.color}`}>
                                            {statusConfig.label}
                                        </span>
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${priorityConfig.color}`}>
                                            {priorityConfig.label}
                                        </span>
                                        <span className="text-sm text-gray-500 capitalize">
                                            {ticket?.category?.replace('_', ' ')}
                                        </span>
                                        {ticket.attachments.length > 0 && (
                                            <div className="flex items-center gap-1">
                                                <Paperclip className="h-4 w-4 text-gray-400" />
                                                <span className="text-sm text-gray-500">{ticket.attachments.length}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                                        <div className="flex items-center gap-4">
                                            {ticket.assignedAgent && (
                                                <div className="flex items-center gap-2">
                                                    <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center">
                                                        <span className="text-blue-600 font-semibold text-xs">
                                                            {ticket.assignedAgent.name.charAt(0)}
                                                        </span>
                                                    </div>
                                                    <span className="text-sm text-gray-600">
                                                        {ticket.assignedAgent.name}
                                                    </span>
                                                </div>
                                            )}
                                            {ticket.requiresCustomerAcknowledgment && (
                                                <div className="flex items-center gap-1">
                                                    <AlertCircle className="h-4 w-4 text-orange-500" />
                                                    <span className="text-sm text-orange-600 font-medium">
                                                        Action Required
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-gray-500">
                                            <Calendar className="h-4 w-4" />
                                            {getTimeAgo(ticket.lastUpdated)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomerSupportPortal;